"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";
import React from "react";
import { useTranslations } from "next-intl";

type PopupType = "success" | "canceled" | "error";

const iconByType: Record<PopupType, React.ReactNode> = {
  success: (
    <div className="flex h-12 w-12 min-w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
      <CheckCircle2 className="h-7 w-7" />
    </div>
  ),
  canceled: (
    <div className="flex h-12 w-12 min-w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Info className="h-7 w-7" />
    </div>
  ),
  error: (
    <div className="flex h-12 w-12 min-w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
      <AlertTriangle className="h-7 w-7" />
    </div>
  ),
};

type ProPlanDialogProps = {
  open: boolean;
  type: PopupType | null;
  sessionId: string | null;
  onOpenChange: (open: boolean) => void;
  onRetry?: () => void;
  onGoToDashboard?: () => void;
};

export const ProPlanDialog: React.FC<ProPlanDialogProps> = ({
  open,
  type,
  onOpenChange,
  onRetry,
  onGoToDashboard,
}) => {
  const t = useTranslations("pro.pricing");
  if (!type) return null;

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleRetry = () => {
    onOpenChange(false);
    onRetry?.();
  };

  const handleGoToDashboard = () => {
    onOpenChange(false);
    if (onGoToDashboard) {
      onGoToDashboard();
    } else {
      window.location.href = "/dashboard/tenant/listing";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            {iconByType[type]}
            <div className="flex flex-col gap-1 text-left">
              <DialogTitle className="text-lg font-semibold">
                {t(`popup.${type}.title`)}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {t(`popup.${type}.message`)}
              </p>
            </div>
          </div>
        </DialogHeader>

        <p className="mt-2 text-sm text-muted-foreground">
          {t(`popup.${type}.extra`)}
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          {type === "success" && (
            <>
              <Button variant="outline" size="sm" onClick={handleClose}>
                {t("popup.close")}
              </Button>
              <Button size="sm" onClick={handleGoToDashboard}>
                {t("popup.go-dashboard")}
              </Button>
            </>
          )}

          {type === "canceled" && (
            <Button size="sm" onClick={handleClose}>
              {t("popup.back-plans")}
            </Button>
          )}

          {type === "error" && (
            <>
              <Button variant="outline" size="sm" onClick={handleClose}>
                {t("popup.close")}
              </Button>
              <Button size="sm" onClick={handleRetry}>
                {t("popup.try-again")}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
